/**
 * Gate I10: Shared Interaction Detail Drawer
 * 
 * A reusable drawer component for displaying interaction details in context.
 * Mirrors the architecture of AnimalDetailDrawer per planning requirements.
 * 
 * Props contract:
 * - open: boolean
 * - onOpenChange: (open: boolean) => void
 * - interaction: The interaction to display (passed from parent)
 * - animalId: string (for fetching animal summary and recent interactions)
 * - onInteractionChange: (interaction: InteractionData) => void (for switching interactions)
 */

import { useState, useCallback, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { useLocation } from "wouter";
import {
  Sheet,
  SheetContent,
} from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  MapPin,
  Calendar,
  Clock,
  User,
  AlertTriangle,
  MessageSquare,
  Sparkles,
  RefreshCw,
  Loader2,
  Info,
  ChevronDown,
  ChevronUp,
  Link as LinkIcon,
  Mail,
  Tag,
} from "lucide-react";
import { apiRequest } from "@/lib/queryClient";
import { useAiEnabled } from "@/hooks/use-ai-enabled";
import { EntityLinkerDialog } from "@/components/tasks/entity-linker-dialog";
import { LinkedTasksList } from "@/components/tasks/linked-tasks-list";
import { useToast } from "@/hooks/use-toast";
import { useZoneLabels } from "@/hooks/use-zone-labels";
import { AnimalSummaryHeader } from "@/components/animals/AnimalSummaryHeader";
import { AnimalDetailsSection } from "@/components/animals/AnimalDetailsSection";
import { HighlightInteractionButton } from "@/components/interactions/HighlightInteractionButton";
import { FlagIndicatorDisplay } from "@/components/interactions/FlagIndicatorDisplay";
import { InteractionNoteRenderer } from "@/components/interactions/InteractionNoteRenderer";
import { FlagReason } from "@/lib/interaction-types";
import { getScoreBandColor } from "@/lib/score-utils";
import { formatDateTimeUTC } from "@/lib/date-utils";

interface InteractionData {
  id: string;
  animalId: string;
  animalName: string | null;
  animalSpecies?: string | null;
  animalPhotoUrl?: string | null;
  kennelStatusNorm: string | null;
  evaluationStatus: string | null;
  locationCode: string;
  zoneCode: string;
  eventType: string;
  scoreCode: string | null;
  scoreBand: string | null;
  occurredAt: string;
  author: string | null;
  rawNote: string | null;
  sourceReportId: string | null;
  sourceEmailId: string | null;
  // Gate R1: Populated by R1 ingest instrumentation. R2 drawer UI will display flag state.
  isFlagged: boolean;
  flagReasons: FlagReason[] | null;
}

interface InteractionDetailDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  interaction: InteractionData | null;
  animalId: string;
  onInteractionChange?: (interaction: InteractionData) => void;
}

interface AnimalData {
  id: string;
  animalId: string;
  name: string;
  species: string;
  photoUrl?: string | null;
  demographics?: {
    intakeDate?: string;
    ageDisplay?: string;
    weightLb?: number;
    originOrgNorm?: string;
  };
  currentStatus?: {
    location?: string;
    kennelStatusNorm?: "AVAILABLE" | "UNAVAIL" | "STRAY_WAIT" | "EVALUATION" | string;
    substatusNorm?: "MEDICAL" | "BEHAVIOR" | "BEH+MED" | string;
    kennelStatusSince?: string | null;
    kennelStatusSinceIsApprox?: boolean;
  };
  evaluation?: {
    status: string | null;
    label: string | null;
    daysInStatus?: number | null;
  };
}

interface RecentInteraction {
  id: string;
  occurredAt: string;
  scoreCode: string | null;
  scoreBand: string | null;
  author: string | null;
  locationCode: string;
}

interface RecentInteractionsResponse {
  animalId: string;
  interactions: RecentInteraction[];
}

interface SummaryContent {
  bullets: string[];
  trendNarrative: string;
}

interface InteractionSummaryStatus {
  status: "disabled" | "unavailable" | "never_generated" | "fresh" | "generating" | "error";
  content: SummaryContent | null;
  errorMessage: string | null;
  lastGeneratedAt: string | null;
}

function formatEventType(eventType: string): string {
  return eventType === "BEH_ALERT" ? "Alert" : "Observation";
}


export function InteractionDetailDrawer({
  open,
  onOpenChange,
  interaction,
  animalId,
  onInteractionChange,
}: InteractionDetailDrawerProps) {
  const [, navigate] = useLocation();
  const [loadingInteractionId, setLoadingInteractionId] = useState<string | null>(null);
  const [isCommentExpanded, setIsCommentExpanded] = useState(false);
  const queryClient = useQueryClient();
  const { isAiEnabled, isLoading: isAiLoading } = useAiEnabled();
  const { resolveLabel } = useZoneLabels();

  const { toast } = useToast();
  const [entityLinkerOpen, setEntityLinkerOpen] = useState(false);

  const { data: animalData, isLoading: animalLoading } = useQuery<AnimalData>({
    queryKey: ['/api/animals', animalId],
    enabled: open && !!animalId,
  });

  const { data: recentData, isLoading: recentLoading } = useQuery<RecentInteractionsResponse>({
    queryKey: ['/api/interactions/recent', animalId],
    enabled: open && !!animalId,
  });

  // AI Insights: Fetch existing summary for this interaction
  const interactionId = interaction?.id;
  const hasNotes = !!interaction?.rawNote && interaction.rawNote.trim().length > 0;
  
  const { data: summaryData, isLoading: summaryLoading } = useQuery<InteractionSummaryStatus>({
    queryKey: ['/api/interactions', interactionId, 'summary'],
    enabled: open && !!interactionId && hasNotes,
  });

  // AI Insights: Generate summary mutation
  const generateMutation = useMutation({
    mutationFn: async () => {
      return await apiRequest("POST", `/api/interactions/${interactionId}/summary/generate`) as InteractionSummaryStatus;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/interactions', interactionId, 'summary'] });
    },
  });

  const recentInteractions = recentData?.interactions?.slice(0, 5) ?? [];

  const allTimeUrl = `/interactions?animalId=${animalId}&startDate=2020-01-01&endDate=${format(new Date(), "yyyy-MM-dd")}`;

  const handleViewAllClick = useCallback(() => {
    navigate(allTimeUrl);
    onOpenChange(false);
  }, [navigate, allTimeUrl, onOpenChange]);

  const handleAuthorClick = useCallback((authorName: string) => {
    const authorUrl = `/interactions?author=${encodeURIComponent(authorName)}&startDate=2020-01-01&endDate=${format(new Date(), "yyyy-MM-dd")}`;
    navigate(authorUrl);
    onOpenChange(false);
  }, [navigate, onOpenChange]);

  const handleRecentInteractionClick = useCallback(async (interactionId: string) => {
    if (!onInteractionChange || interactionId === interaction?.id) return;
    
    setLoadingInteractionId(interactionId);
    setIsCommentExpanded(false);
    try {
      const fullInteraction = await apiRequest("GET", `/api/interactions/${interactionId}`) as InteractionData;
      onInteractionChange(fullInteraction);
    } catch (error) {
      console.error("Failed to fetch interaction:", error);
    } finally {
      setLoadingInteractionId(null);
    }
  }, [interaction?.id, onInteractionChange]);

  const { commentPreview, hasMoreLines } = useMemo(() => {
    const rawNote = interaction?.rawNote;
    if (!rawNote) return { commentPreview: null, hasMoreLines: false };
    
    // Approximate 10 visual lines at ~55 chars per line in the drawer width
    const maxChars = 550;
    
    if (rawNote.length <= maxChars) {
      return { commentPreview: rawNote, hasMoreLines: false };
    }
    
    // Find a good break point (word boundary) near the limit
    let breakPoint = maxChars;
    const spaceIndex = rawNote.lastIndexOf(' ', maxChars);
    if (spaceIndex > maxChars - 50) {
      breakPoint = spaceIndex;
    }
    
    return {
      commentPreview: rawNote.slice(0, breakPoint) + '...',
      hasMoreLines: true,
    };
  }, [interaction?.rawNote]);

  return (
    <>
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent 
        className="!w-full max-w-full sm:!w-[520px] sm:max-w-[520px] p-0 flex flex-col"
        data-testid="interaction-detail-drawer"
      >
        <AnimalSummaryHeader
          animalId={animalId}
          name={interaction?.animalName ?? animalData?.name ?? null}
          species={interaction?.animalSpecies ?? animalData?.species ?? null}
          photoUrl={animalData?.photoUrl ?? null}
          isLoading={animalLoading}
          onClose={() => onOpenChange(false)}
          showFollowButton={true}
          drawerIcon={MessageSquare}
          drawerLabel="Interaction details"
        />

        <ScrollArea className="flex-1">
          <div className="p-6 space-y-6">
            <AnimalDetailsSection
              animalId={animalId}
              animalData={animalData ? {
                name: animalData.name,
                species: animalData.species,
                photoUrl: animalData.photoUrl ?? null,
                intakeDate: animalData.demographics?.intakeDate ?? null,
                currentStatus: animalData.currentStatus,
                evaluation: animalData.evaluation,
              } : null}
              isLoading={animalLoading}
            />

            <Separator />

            <div data-testid="interaction-drawer-details">
              <div className="flex items-center justify-between gap-2 flex-wrap mb-3">
                <div className="flex items-center gap-2">
                  <h3 className="font-semibold">This Interaction</h3>
                  {interaction && (
                    <Badge 
                      variant={interaction.eventType === "BEH_ALERT" ? "alert" : "secondary"}
                      className="text-xs"
                    >
                      {formatEventType(interaction.eventType)}
                    </Badge>
                  )}
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  {interaction && (
                    <HighlightInteractionButton
                      variant="icon"
                      interactionId={interaction.id}
                      isHighlighted={
                        (interaction.flagReasons ?? []).some(
                          (r) => r.type === "highlighted"
                        )
                      }
                      animalId={interaction.animalId}
                    />
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    className="gap-1.5"
                    onClick={() => setEntityLinkerOpen(true)}
                    data-testid="interaction-drawer-add-to"
                  >
                    <LinkIcon className="h-3.5 w-3.5" />
                    Add to...
                  </Button>
                </div>
              </div>
              {interaction ? (
                <div className="space-y-3 text-sm">
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-muted-foreground" />
                    <span>Date: {formatDateTimeUTC(interaction.occurredAt)}</span>
                  </div>

                  <div className="flex items-center gap-2">
                    <MapPin className="h-4 w-4 text-muted-foreground" />
                    <span>
                      Location: {interaction.locationCode}
                      {interaction.zoneCode && ` (${resolveLabel(interaction.zoneCode)})`}
                    </span>
                  </div>

                  <div className="flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                    <span className="font-medium">Score:</span>
                    {interaction.scoreCode ? (
                      <div className="flex items-center gap-1.5">
                        <span className="font-semibold text-base">{interaction.scoreCode}</span>
                        {interaction.scoreBand && (
                          <Badge 
                            variant="outline" 
                            className={`text-xs px-1.5 py-0 h-5 ${getScoreBandColor(interaction.scoreBand)}`}
                          >
                            {interaction.scoreBand}
                          </Badge>
                        )}
                      </div>
                    ) : (
                      <span className="text-muted-foreground italic">No score recorded</span>
                    )}
                  </div>

                  <div className="flex items-center gap-2">
                    <User className="h-4 w-4 text-muted-foreground" />
                    <span>Author: </span>
                    {interaction.author ? (
                      <span
                        className="text-primary cursor-pointer hover:underline"
                        onClick={() => handleAuthorClick(interaction.author!)}
                        role="link"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleAuthorClick(interaction.author!);
                          }
                        }}
                        data-testid="interaction-drawer-author-link"
                      >
                        {interaction.author}
                      </span>
                    ) : (
                      <span className="text-muted-foreground">Unknown</span>
                    )}
                  </div>

                  {interaction.sourceEmailId && (
                    <div className="flex items-center gap-2" data-testid="interaction-drawer-source-email">
                      <Mail className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                      <span className="text-muted-foreground">Source:</span>
                      <button
                        type="button"
                        className="text-xs text-primary hover:underline"
                        onClick={() => navigate(`/admin/settings?tab=email-ingest&highlight=${interaction.sourceEmailId}`)}
                        data-testid="interaction-drawer-source-email-link"
                      >
                        View source email
                      </button>
                    </div>
                  )}

                  {interaction.flagReasons && interaction.flagReasons.some(r => r.type === "long_note" || r.type === "keyword") && (
                    <div className="flex items-start gap-2" data-testid="interaction-drawer-flags">
                      <Tag className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" />
                      <div className="space-y-1">
                        <span className="font-medium">Flags</span>
                        <FlagIndicatorDisplay
                          flagReasons={interaction.flagReasons}
                          filterTypes={["long_note", "keyword"]}
                        />
                      </div>
                    </div>
                  )}

                  {interaction.rawNote && (
                    <div className="mt-4" data-testid="interaction-drawer-comment">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-medium text-sm">Comment</h4>
                      </div>
                      <div className="p-3 bg-muted/50 rounded-md">
                        <InteractionNoteRenderer
                          note={isCommentExpanded ? interaction.rawNote : commentPreview}
                          flagReasons={interaction.flagReasons}
                        />
                        {hasMoreLines && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="mt-2 h-7 px-2 text-xs text-muted-foreground hover:text-foreground"
                            onClick={() => setIsCommentExpanded(!isCommentExpanded)}
                            data-testid="interaction-drawer-comment-toggle"
                          >
                            {isCommentExpanded ? (
                              <>
                                <ChevronUp className="h-3 w-3 mr-1" />
                                View less
                              </>
                            ) : (
                              <>
                                <ChevronDown className="h-3 w-3 mr-1" />
                                View more
                              </>
                            )}
                          </Button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">No interaction selected</p>
              )}
            </div>

            <Separator />

            {/* Recent Interactions Section - above linked tasks/meetings */}
            <div data-testid="interaction-drawer-recent">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold">Recent Interactions</h3>
                <span
                  className="text-xs text-muted-foreground cursor-pointer hover-elevate px-1 rounded"
                  onClick={handleViewAllClick}
                  role="link"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleViewAllClick();
                    }
                  }}
                  data-testid="interaction-drawer-view-all"
                >
                  View All Interactions
                </span>
              </div>
              {recentLoading ? (
                <div className="space-y-2">
                  <Skeleton className="h-10 w-full" />
                  <Skeleton className="h-10 w-full" />
                  <Skeleton className="h-10 w-full" />
                </div>
              ) : recentInteractions.length > 0 ? (
                <div className="space-y-2" data-testid="interaction-drawer-recent-list">
                  {recentInteractions.map((item, idx) => {
                    const isSelected = interaction?.id === item.id;
                    const isLoading = loadingInteractionId === item.id;
                    const isClickable = onInteractionChange && !isSelected && !isLoading;
                    return (
                      <div 
                        key={item.id} 
                        className={`flex items-center justify-between gap-2 p-2 rounded-md text-sm transition-colors ${
                          isSelected 
                            ? 'bg-accent/20 border border-accent/40' 
                            : isClickable 
                              ? 'bg-muted/30 hover-elevate cursor-pointer' 
                              : 'bg-muted/30'
                        } ${isLoading ? 'opacity-50' : ''}`}
                        data-testid={`interaction-drawer-recent-row-${idx}`}
                        onClick={() => isClickable && handleRecentInteractionClick(item.id)}
                        role={isClickable ? "button" : undefined}
                        tabIndex={isClickable ? 0 : undefined}
                        onKeyDown={(e) => {
                          if (isClickable && (e.key === 'Enter' || e.key === ' ')) {
                            e.preventDefault();
                            handleRecentInteractionClick(item.id);
                          }
                        }}
                      >
                        <div className="flex flex-col gap-0.5 min-w-0">
                          <div className="flex items-center gap-2">
                            <Clock className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                            <span className="text-xs">{formatDateTimeUTC(item.occurredAt)}</span>
                            {isSelected && (
                              <Badge variant="secondary" className="text-xs px-1.5 py-0 h-4">
                                Viewing
                              </Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-2 text-xs text-muted-foreground ml-5">
                            <span>{item.author || "Unknown"}</span>
                            <span>|</span>
                            <span>{item.locationCode}</span>
                          </div>
                        </div>
                        <div className="flex-shrink-0">
                          {item.scoreCode ? (
                            <div className="flex items-center gap-1">
                              <span className="text-xs font-medium">{item.scoreCode}</span>
                              {item.scoreBand && (
                                <Badge 
                                  variant="outline" 
                                  className={`text-xs px-1 py-0 h-4 ${getScoreBandColor(item.scoreBand)}`}
                                >
                                  {item.scoreBand}
                                </Badge>
                              )}
                            </div>
                          ) : (
                            <span className="text-xs text-muted-foreground">No score</span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">No recent interactions</p>
              )}
            </div>

            <Separator />

            {interaction && (
              <LinkedTasksList entityType="interaction" entityId={interaction.id} compact />
            )}

            {/* AI Insights Section - skeleton while loading, hidden when AI disabled */}
            {hasNotes && (isAiLoading ? (
              <>
                <Separator />
                <div className="space-y-3" data-testid="skeleton-ai-insights">
                  <div className="flex items-center gap-2">
                    <Skeleton className="h-4 w-4" />
                    <Skeleton className="h-4 w-24" />
                  </div>
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-3/4" />
                </div>
              </>
            ) : isAiEnabled ? (
              <>
                <Separator />
                <div data-testid="interaction-drawer-ai-insights">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold flex items-center gap-2">
                        <Sparkles className="h-4 w-4 text-primary" />
                        AI Insights
                      </h3>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            type="button"
                            className="text-muted-foreground hover:text-foreground transition-colors"
                            data-testid="ai-insights-info-tooltip"
                          >
                            <Info className="h-3.5 w-3.5" />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent side="bottom" className="max-w-xs text-xs space-y-1">
                          <p>This insight is AI-generated from this interaction's notes.</p>
                          <p>You can refine terminology and tone in Admin Settings → AI (System Instructions, Style Guidance, and Model).</p>
                          <p className="text-muted-foreground">The insight is non-authoritative and may be incomplete.</p>
                        </TooltipContent>
                      </Tooltip>
                    </div>
                    {summaryData?.status === "fresh" && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 px-2 text-xs"
                        onClick={() => generateMutation.mutate()}
                        disabled={generateMutation.isPending}
                        data-testid="interaction-drawer-regenerate-insights"
                      >
                        {generateMutation.isPending ? (
                          <Loader2 className="h-3 w-3 animate-spin mr-1" />
                        ) : (
                          <RefreshCw className="h-3 w-3 mr-1" />
                        )}
                        Regenerate
                      </Button>
                    )}
                  </div>

                  {summaryLoading || generateMutation.isPending || summaryData?.status === "generating" ? (
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        <span>Generating insights...</span>
                      </div>
                      <Skeleton className="h-4 w-full" />
                      <Skeleton className="h-4 w-3/4" />
                      <Skeleton className="h-4 w-5/6" />
                    </div>
                  ) : summaryData?.status === "disabled" ? (
                    <p className="text-sm text-muted-foreground">
                      AI insights are currently disabled.
                    </p>
                  ) : summaryData?.status === "unavailable" ? (
                    <p className="text-sm text-muted-foreground">
                      {summaryData.errorMessage || "AI insights are not available."}
                    </p>
                  ) : summaryData?.status === "error" ? (
                    <div className="space-y-2">
                      <p className="text-sm text-destructive">
                        Error: {summaryData.errorMessage || "Failed to generate insights."}
                      </p>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => generateMutation.mutate()}
                        disabled={generateMutation.isPending}
                        data-testid="interaction-drawer-retry-insights"
                      >
                        {generateMutation.isPending ? (
                          <Loader2 className="h-3 w-3 animate-spin mr-1" />
                        ) : null}
                        Retry
                      </Button>
                    </div>
                  ) : summaryData?.status === "fresh" && summaryData.content ? (
                    <div className="space-y-3">
                      <ul className="space-y-1.5 text-sm">
                        {summaryData.content.bullets.map((bullet, idx) => (
                          <li key={idx} className="flex items-start gap-2">
                            <span className="text-primary mt-1">•</span>
                            <span>{bullet}</span>
                          </li>
                        ))}
                      </ul>
                      {summaryData.content.trendNarrative && (
                        <p className="text-sm text-muted-foreground italic">
                          {summaryData.content.trendNarrative}
                        </p>
                      )}
                      {summaryData.lastGeneratedAt && (
                        <p className="text-xs text-muted-foreground italic text-right">
                          Generated {format(new Date(summaryData.lastGeneratedAt), "MMM d, yyyy 'at' h:mm a")}
                        </p>
                      )}
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <p className="text-sm text-muted-foreground">
                        Generate AI-powered insights about this interaction.
                      </p>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => generateMutation.mutate()}
                        disabled={generateMutation.isPending}
                        className="gap-1.5"
                        data-testid="interaction-drawer-generate-insights"
                      >
                        {generateMutation.isPending ? (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        ) : (
                          <Sparkles className="h-3 w-3" />
                        )}
                        {generateMutation.isPending ? "Generating..." : "Generate Insights"}
                      </Button>
                    </div>
                  )}
                </div>
              </>
            ) : null)}
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>

      {interaction && (
        <EntityLinkerDialog
          open={entityLinkerOpen}
          onOpenChange={setEntityLinkerOpen}
          entityType="interaction"
          entityId={interaction.id}
          entityLabel={`${interaction.eventType || "Interaction"} — ${interaction.occurredAt ? new Date(interaction.occurredAt).toLocaleDateString() : ""}`.trim()}
          animalId={interaction.animalId}
        />
      )}
    </>
  );
}

export default InteractionDetailDrawer;
